import './assets/main.css'
import 'primeicons/primeicons.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { CustomTheme } from './utils/primevue-theme.ts'
import * as Sentry from '@sentry/vue'
import { scrubSecrets } from './utils/sentryScrub'

import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import Divider from 'primevue/divider'
import { i18n } from './i18n'
import { installToastDefaults } from './utils/toastDefaults'
import {
  currentTenantCode,
  followTenantSwitches,
  handedOverTenant,
  keepTenantForTab,
  TENANT_PARAM,
} from './utils/tenant'

const app = createApp(App)

Sentry.init({
  app,
  // No token of a password link or a hand-off, no secret, leaves for Sentry
  beforeSend: (event) => scrubSecrets(event),
  beforeSendTransaction: (event) => scrubSecrets(event),
  beforeBreadcrumb: (breadcrumb) => scrubSecrets(breadcrumb),
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({ beforeAddRecordingEvent: (event) => scrubSecrets(event) }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
})

// A session replay's event skips beforeSend: its page addresses (a password link, the
// hand-off token) reach Sentry scrubbed only through an event processor
Sentry.addEventProcessor((event) => scrubSecrets(event))

app.use(createPinia())
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: CustomTheme,
    options: {
      prefix: 'p',
      darkModeSelector: 'light',
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue',
      },
    },
  },
})
app.use(i18n)
app.component('Button', Button)
app.component('Toast', Toast)
app.component('Dialog', Dialog)
app.component('InputText', InputText)
app.component('InputNumber', InputNumber)
app.component('Select', Select)
app.component('Checkbox', Checkbox)
app.component('Divider', Divider)
app.use(ToastService)
// Successes and info close themselves; errors and warnings stay (V-01).
// Must follow app.use(ToastService) — it patches the service that creates.
installToastDefaults(app)
app.directive('tooltip', Tooltip)

// If the admin hub handed off a `?customer_token=...`, exchange it for a real
// session before mounting so the session is live for the first navigation.
// We still mount on failure (the user lands on login).
const authStore = useAuthStore()
// On dev / QA every tab follows a tenant switch made in another one
followTenantSwitches()
const tenant = handedOverTenant()
if (tenant !== null) {
  // The admin hub's token belongs to another tenant's backend: switch this tab to it
  // (not the other tabs), signing out here as the tenant switcher does, and take the
  // hand-off up after the reload.
  authStore.clearSession()
  keepTenantForTab(tenant)
  window.location.reload()
} else {
  // A hand-off on this tenant: the session stays on it, whatever other tabs switch to
  const here = currentTenantCode()
  if (here !== null && new URLSearchParams(window.location.search).has(TENANT_PARAM)) {
    keepTenantForTab(here)
  }
  authStore
    .ingestAdminHubTokenFromUrl()
    .catch(() => {})
    .finally(() => {
      // The router's initial navigation already resolved (to /login) before the
      // exchange finished, so on success send the now-authenticated session to
      // home — HomeView then fetches its data.
      if (authStore.isAdminHubSession) {
        router.replace({ name: 'home' }).catch(() => {})
      }
      app.mount('#app')
    })
}
